import { useCallback, useEffect, useMemo, useState } from 'react';

import dayjs, { Dayjs } from 'dayjs';

import { EVENTS } from '@/analytics/consts';
import { useAnalytics } from '@/analytics/useAnalytics';
import {
  MeetingBotDto,
  MeetingBotDtoStatus,
  useBulkScheduleMeetingBot,
  useCancelMeetingBot,
  useScheduleMeetingBot,
  useSearchMeetingBots,
  useGetGrantForUser,
  MeetingBotResponse
} from '@/api/generated';
import { CalendarEventWithSchedule } from '@/components/CalendarEvents/types';
import {
  createScheduleObject,
  getIsEventInviteable,
  getLatestScheduledMeetingBot
} from '@/components/CalendarEvents/utils';
import useLoginContext from '@/context/LoginFlowContextProvider/useLoginContext';
import useCalendar from '@/hooks/useCalendar';
import useMeetings from '@/hooks/useMeetings';

export const useCalendarEventsLogic = (value: Dayjs | null) => {
  const { currentUser } = useLoginContext();
  const { mutateAsync: cancelMeetingBot } = useCancelMeetingBot();
  const { mutateAsync: scheduleMeetingBot } = useScheduleMeetingBot();
  const { mutate: bulkScheduleMeetingBot } = useBulkScheduleMeetingBot();
  const { mutateAsync: searchMeetingBots } = useSearchMeetingBots();
  const { data: userGrantResponse } = useGetGrantForUser();
  const startOfDay = dayjs(value).startOf('day').valueOf();
  const endOfDay = dayjs(value).endOf('day').valueOf();
  const { captureEvent } = useAnalytics();
  const [isLoadingInviteAll, setIsLoadingInviteAll] = useState<boolean>(false);
  const [loadingMeetingBots, setLoadingMeetingBots] = useState<boolean>(true);
  const { eventList, eventListLoading } = useCalendar(startOfDay, endOfDay);
  const { meetingList, meetingListLoading } = useMeetings(startOfDay, endOfDay, undefined);

  const [eventListWithSchedule, setEventListWithSchedule] = useState<CalendarEventWithSchedule[]>(
    []
  );

  const shouldShowInviteToAllButton = useMemo(
    () => eventListWithSchedule.some(event => event.isInviteable),
    [eventListWithSchedule]
  );

  const getMeetingsBots = useCallback(async (): Promise<MeetingBotDto[]> => {
    if (eventList.length <= 0) {
      setLoadingMeetingBots(false);
      return [];
    }
    let ret: MeetingBotDto[] = [];
    const calendarEventIds = eventList
      .map(event => event.id)
      .filter((id): id is string => id !== undefined);

    if (calendarEventIds.length > 0) {
      const data = await searchMeetingBots(
        { data: { calendarEventIds: calendarEventIds } },
        {
          onSettled: () => {
            setLoadingMeetingBots(false);
          }
        }
      );
      ret = data.items || [];
    }
    return ret;
  }, [eventList, searchMeetingBots]);

  useEffect(() => {
    const updateEventList = async () => {
      const bots = await getMeetingsBots();
      setEventListWithSchedule(
        eventList.map(event => ({
          ...event,
          isInviteable: getIsEventInviteable(
            event,
            currentUser,
            userGrantResponse?.items?.[0]?.email
          ),
          isScheduled:
            getLatestScheduledMeetingBot(event.id, bots)?.status === MeetingBotDtoStatus.scheduled,
          latestBotId: getLatestScheduledMeetingBot(event.id, bots)?.botId || ''
        }))
      );
    };
    updateEventList();
  }, [eventList, getMeetingsBots, currentUser, userGrantResponse]);

  const captureBotScheduled = (response: MeetingBotResponse) => {
    const eventData = response.items?.map(bot => ({
      calendarEventId: bot.calendarEventId,
      botId: bot.botId
    }));
    captureEvent(EVENTS.MEETING_BOT_SCHEDULED, {
      eventData: eventData
    });
  };

  const scheduleBot = useCallback(
    async (event: CalendarEventWithSchedule): Promise<void> => {
      if (isLoadingInviteAll || !event.conferencing?.url) return;
      setEventListWithSchedule(prev =>
        prev.map(ev => ({
          ...ev,
          ...(ev.id === event.id ? { isScheduled: true } : {})
        }))
      );
      const request = createScheduleObject(event, currentUser);
      await scheduleMeetingBot(
        { data: request },
        {
          onError: () => {
            setEventListWithSchedule(prev =>
              prev.map(ev => ({
                ...ev,
                ...(ev.id === event.id ? { isScheduled: false } : {})
              }))
            );
          },
          onSuccess: (response: MeetingBotResponse) => {
            captureBotScheduled(response);
          }
        }
      );
    },
    [scheduleMeetingBot, setEventListWithSchedule, isLoadingInviteAll, currentUser]
  );

  const scheduleAll = useCallback(async () => {
    setIsLoadingInviteAll(true);
    const eventListCopy = [...eventListWithSchedule];
    setEventListWithSchedule(prev =>
      prev.map(ev => ({
        ...ev,
        isScheduled: true
      }))
    );

    const inviteList = eventListWithSchedule
      .filter(event => event.isInviteable)
      .map(event => createScheduleObject(event, currentUser));

    bulkScheduleMeetingBot(
      { data: { requests: inviteList } },
      {
        onError: () => {
          setEventListWithSchedule(eventListCopy);
        },
        onSuccess: (bots: MeetingBotResponse) => {
          captureBotScheduled(bots);
        },
        onSettled: () => {
          setIsLoadingInviteAll(false);
        }
      }
    );
  }, [eventListWithSchedule, bulkScheduleMeetingBot, currentUser]);

  const removeBot = useCallback(
    async (event: CalendarEventWithSchedule): Promise<void> => {
      if (isLoadingInviteAll) return;
      setEventListWithSchedule(prev =>
        prev.map(ev => ({
          ...ev,
          ...(ev.id === event.id ? { isScheduled: false } : {})
        }))
      );
      const meetingBots = await getMeetingsBots();
      await cancelMeetingBot(
        {
          data: {
            botId: getLatestScheduledMeetingBot(event.id, meetingBots)?.botId,
            calendarEventId: event.id,
            calendarMasterEventId: event.masterEventId
          }
        },
        {
          onError: () => {
            setEventListWithSchedule(prev =>
              prev.map(ev => ({
                ...ev,
                ...(ev.id === event.id ? { isScheduled: true } : {})
              }))
            );
          }
        }
      );
    },
    [cancelMeetingBot, setEventListWithSchedule, getMeetingsBots, isLoadingInviteAll]
  );

  const handleClickInviteToAll = () => {
    if (isLoadingInviteAll) return;
    const isAllScheduled = eventListWithSchedule.every(event => event.isScheduled);
    if (!isAllScheduled) {
      scheduleAll();
    }
  };

  const isLoading = useMemo(
    () => eventListLoading || meetingListLoading || loadingMeetingBots,
    [eventListLoading, meetingListLoading, loadingMeetingBots]
  );

  return {
    isLoadingInviteAll,
    eventListWithSchedule,
    meetingList,
    shouldShowInviteToAllButton,
    handleClickInviteToAll,
    scheduleBot,
    removeBot,
    setEventListWithSchedule,
    isLoading
  };
};
